import { useTheme } from '@context/ThemeContext';
import React from 'react';

type GridBackgroundLayoutProps = {
  children: React.ReactNode;
  cellSize?: number;
  speedSec?: number;
  lineColor?: string;
  lineOpacity?: number;
  glowOpacity?: number;
  glowBlur?: number;
  boomerang?: boolean;
  vignette?: boolean;
  disableOnMobile?: boolean;
  mobileBreakpoint?: number;
  className?: string;
  contentClassName?: string;
};

export function GridBackgroundLayout({
  children,
  cellSize = 5,
  speedSec = 1,
  lineColor = 'rgba(77, 75, 75, 0.65)',
  lineOpacity = 0.22,
  glowOpacity = 0.18,
  glowBlur = 1,
  boomerang = false,
  vignette = true,
  disableOnMobile = false,
  mobileBreakpoint = 767,
  className,
  contentClassName,
}: GridBackgroundLayoutProps) {
  const id = React.useId();
  const { colors } = useTheme();
  const [reduced, setReduced] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mobileBreakpoint]);

  const shouldRenderGrid = !(disableOnMobile && isMobile);

  const animateProps = reduced
    ? null
    : boomerang
      ? { values: `0 0; 0 ${cellSize}; 0 0` }
      : { from: '0 0', to: `0 ${cellSize}` };

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: colors.bg_400 ?? 'black',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        {shouldRenderGrid ? (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1000 600"
            preserveAspectRatio="none"
            style={{ display: 'block' }}
          >
            <defs>
              <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation={glowBlur} result="b" />
                <feColorMatrix
                  in="b"
                  type="matrix"
                  values="
                  1 0 0 0 0
                  0 1 0 0 0
                  0 0 1 0 0
                  0 0 0 12 -4"
                  result="g"
                />
                <feMerge>
                  <feMergeNode in="g" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <pattern
                id={`${id}-grid`}
                width={cellSize}
                height={cellSize}
                patternUnits="userSpaceOnUse"
              >
                {!reduced && (
                  <animateTransform
                    attributeName="patternTransform"
                    type="translate"
                    dur={`${speedSec}s`}
                    repeatCount="indefinite"
                    {...(animateProps || {})}
                  />
                )}

                <path
                  d={`M ${cellSize} 0 L 0 0 0 ${cellSize}`}
                  fill="none"
                  stroke={lineColor}
                  strokeWidth="1"
                  opacity={lineOpacity}
                />
              </pattern>

              {vignette && (
                <radialGradient id={`${id}-v`} cx="50%" cy="50%" r="70%">
                  <stop offset="0%" stopColor="rgba(0,0,0,0.65)" />
                  <stop offset="70%" stopColor="rgba(0,0,0,0.65)" />
                  <stop offset="100%" stopColor="rgba(0,0,0,0.65)" />
                </radialGradient>
              )}
            </defs>

            <rect width="1000" height="600" fill={`url(#${id}-grid)`} />

            <rect
              width="1000"
              height="600"
              fill={`url(#${id}-grid)`}
              opacity={glowOpacity}
              filter={`url(#${id}-glow)`}
            />

            {vignette && <rect width="1000" height="600" fill={`url(#${id}-v)`} />}
          </svg>
        ) : null}
      </div>

      <div
        className={contentClassName}
        style={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
        }}
      >
        {children}
      </div>
    </div>
  );
}
